import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { CmsFormField } from '@domgen/dgx-fe-business-models';
import {
  DynamicFormbuilderService,
  FieldDef,
} from '@domgen/dgx-fe-dynamic-form-builder';
import { of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { PersonalDetailsFormConfigService } from './personal-details-form-config.service';
import { PersonalDetailsState } from '@common/util-models';

export interface PersonalDetailsFormState {
  formBuilderConfig: FieldDef[];
  cmsFormData: CmsFormField[];
  form: FormGroup;
  validate: boolean;
  prefilledForm: PersonalDetailsState | undefined;
}

@Injectable()
export class PersonalDetailsFormStateService extends ComponentStore<PersonalDetailsFormState> {
  // Selectors
  readonly formBuilderConfig$ = this.select(
    (state: PersonalDetailsFormState) => state.formBuilderConfig
  );
  readonly formGroup$ = this.select(
    (state: PersonalDetailsFormState) => state.form
  );
  readonly cmsFormData$ = this.select(
    (state: PersonalDetailsFormState) => state.cmsFormData
  );

  readonly validate$ = this.select(
    (state: PersonalDetailsFormState) => state.validate
  );

  readonly prefilledForm$ = this.select(
    (state: PersonalDetailsFormState) => state.prefilledForm
  );

  readonly setCmsFormData = this.updater(
    (state: PersonalDetailsFormState, value: CmsFormField[]) => ({
      ...state,
      cmsFormData: value,
    })
  );

  readonly setPrefilledFormData = this.updater(
    (state: PersonalDetailsFormState, value: PersonalDetailsState) => ({
      ...state,
      prefilledForm: value,
    })
  );

  readonly validate = this.updater((state: PersonalDetailsFormState) => ({
    ...state,
    validate: true,
  }));

  readonly valueChanges$ = this.formGroup$.pipe(
    switchMap((formGroup) => formGroup.valueChanges)
  );

  // *** Updater Sources ****
  private fieldDefinitionUpdater$ = this.cmsFormData$.pipe(
    switchMap((cmsData) =>
      of(cmsData).pipe(
        withLatestFrom(this.prefilledForm$),
        map(([formFieldsData, prefilledForm]) => {
          return formFieldsData.length
            ? this.formConfig.getFormbuilderConfig(
                formFieldsData,
                prefilledForm
              )
            : [];
        })
      )
    )
  );

  private readonly cmsFieldDefinitionUpdater = this.updater(
    (state: PersonalDetailsFormState, value: FieldDef[]) => ({
      ...state,
      formBuilderConfig: value,
    })
  )(this.fieldDefinitionUpdater$);

  private formGroupUpdater$ = this.formBuilderConfig$.pipe(
    switchMap((formBuilderConfig) =>
      of(formBuilderConfig).pipe(
        map((conf) => this.dynamicFormBuilder.generateFormGroup(conf))
      )
    )
  );

  private readonly formGroupUpdater = this.updater(
    (state: PersonalDetailsFormState, value: FormGroup) => ({
      ...state,
      form: value,
    })
  )(this.formGroupUpdater$);

  readonly vm$ = this.select(
    this.formBuilderConfig$,
    this.formGroup$,
    this.validate$,
    (fieldDef, formGroup, validate) => ({
      fieldDef,
      formGroup,
      validate,
    })
  );

  constructor(
    private formConfig: PersonalDetailsFormConfigService,
    private dynamicFormBuilder: DynamicFormbuilderService
  ) {
    super({
      formBuilderConfig: [],
      cmsFormData: [],
      form: new FormGroup({}),
      validate: false,
      prefilledForm: undefined,
    });
  }
}
